/**
 * The words and colours of worker health, shared by the header
 * `WorkerHealthBadge` and the `/admin/workers` page.
 *
 * One table on purpose: the badge says "2 LAGGING" and links to the page, so
 * the page must say LAGGING too — a reader who arrives from the header has to
 * find the word they clicked on. Plain-English explanations live in sentences
 * and tooltips; the label itself is the badge's word.
 *
 * `rank` is urgency, worst first — the same order `analytics.worker_health`
 * sorts by and the badge picks its single worst bucket with.
 */

export type WorkerHealthState =
  | "OK"
  | "LAGGING"
  | "DEGRADED"
  | "FAILING"
  | "DOWN"
  | "NEVER_RAN"
  | "STALE_DATA";

export type HealthBucketStyle = {
  /** The label, exactly as the header badge prints it. */
  word: string;
  /** 1 = most urgent. */
  rank: number;
  /** Solid fill for a status dot. */
  dot: string;
  /** Tinted background for a pill. */
  chip: string;
  /** Text colour on that tint. */
  text: string;
};

export const HEALTH_BUCKETS: Record<WorkerHealthState, HealthBucketStyle> = {
  DOWN:       { word: "DOWN",      rank: 1, dot: "bg-red-600",    chip: "bg-red-600/15",    text: "text-red-200" },
  FAILING:    { word: "FAILING",   rank: 2, dot: "bg-red-500",    chip: "bg-red-500/15",    text: "text-red-300" },
  STALE_DATA: { word: "STALE",     rank: 3, dot: "bg-purple-400", chip: "bg-purple-400/10", text: "text-purple-300" },
  // Text one step lighter than the dot: #e35e1c on its own tint is ~4.1:1, under AA for 11px type.
  DEGRADED:   { word: "DEGRADED",  rank: 4, dot: "bg-[#e35e1c]",  chip: "bg-[#e35e1c]/10",  text: "text-orange-300" },
  LAGGING:    { word: "LAGGING",   rank: 5, dot: "bg-yellow-400", chip: "bg-yellow-400/10", text: "text-yellow-300" },
  NEVER_RAN:  { word: "NEVER RAN", rank: 6, dot: "bg-gray-400",   chip: "bg-gray-400/10",   text: "text-gray-300" },
  OK:         { word: "OK",        rank: 7, dot: "bg-[#a4d007]",  chip: "bg-[#a4d007]/10",  text: "text-[#a4d007]" },
};

const KNOWN = new Set<string>(Object.keys(HEALTH_BUCKETS));

/** Narrow an untrusted string (a DB value) to a known state. */
export function toHealthState(value: unknown): WorkerHealthState | null {
  return typeof value === "string" && KNOWN.has(value)
    ? (value as WorkerHealthState)
    : null;
}
